// #1755: a task's model reads by the portal's mode wherever it shows. Standard names it from the connected providers'
// list (`GET /v1/llm/models`) and calls the unset default "Recommended"; Advanced shows the exact model ID. The Model
// field on both task forms and the task page's header chip ask the same question, so they share one loader and one
// set of labels rather than copies of the fetch-and-fall-back dance (as `lib/llmTiers.ts` does for tiers).

import { ref } from 'vue'
import { api, type LlmDirectoryModel } from '@/api'
import type { UiMode } from '@/composables/useUiMode'

/** The unset model: the task runs the enterprise default, which the list names. */
export const RECOMMENDED_LABEL = 'Recommended'

/** A saved model the connected providers' list does not offer now. Standard never shows its ID. */
export const UNLISTED_MODEL_LABEL = 'Unlisted model'

/**
 * A saved model when the list could not be read: nobody knows whether the providers still offer it, so Standard
 * neither calls it unlisted nor shows its ID.
 */
export const UNKNOWN_MODEL_LABEL = 'Saved model'

/** Shown for a saved model while the list is still loading, so Standard never flashes an ID or a wrong label. */
export const LOADING_MODEL_LABEL = '…'

/** One directory model as an option reads: its name in Standard, `Name (id)` in Advanced. A free model says so. */
export function modelOptionLabel(model: LlmDirectoryModel, mode: UiMode): string {
    let base = model.name
    if (mode === 'advanced') {
        base = model.name === model.id ? model.id : `${model.name} (${model.id})`
    }
    return model.free ? `${base} · free` : base
}

export function useLlmModels() {
    const models = ref<LlmDirectoryModel[]>([])
    const defaultModel = ref<string | null>(null)
    /** True once a fetch has finished, whether it answered or failed: the loading state ends either way. */
    const settled = ref(false)
    /** True when the last fetch failed, so an empty list says nothing about which models the providers offer. */
    const failed = ref(false)

    async function loadLlmModels(): Promise<void> {
        failed.value = false
        try {
            const directory = await api.llmModels()
            models.value = Array.isArray(directory?.models) ? directory.models : []
            defaultModel.value = directory?.defaultModel ?? null
        } catch {
            // No connection, or the list is down: a task can still be saved on Recommended, and a saved model is kept.
            models.value = []
            defaultModel.value = null
            failed.value = true
        } finally {
            settled.value = true
        }
    }

    /** The listed model with this ID, or null when the list does not offer it (or has not loaded). */
    function findModel(id: string | null | undefined): LlmDirectoryModel | null {
        if (!id) {
            return null
        }
        return models.value.find(model => model.id === id) ?? null
    }

    /**
     * What the Recommended default resolves to, as this mode names it: the default's name in Standard, its ID in
     * Advanced, or '' when that cannot be said (the list is down, or does not name the default).
     */
    function recommendedModelLabel(mode: UiMode): string {
        if (!defaultModel.value) {
            return ''
        }
        if (mode === 'advanced') {
            return defaultModel.value
        }
        return findModel(defaultModel.value)?.name ?? ''
    }

    /** The Recommended choice: `Recommended — <default>` when the default can be named in this mode, else `Recommended`. */
    function recommendedLabel(mode: UiMode): string {
        const resolved = recommendedModelLabel(mode)
        return resolved ? `${RECOMMENDED_LABEL} — ${resolved}` : RECOMMENDED_LABEL
    }

    /**
     * A saved model the list does not offer: its ID in Advanced. In Standard `…` until the list answers, `Saved model`
     * when the read failed (it may well still be offered), and `Unlisted model` when the list answered without it.
     */
    function unlistedModelLabel(id: string, mode: UiMode): string {
        if (mode === 'advanced') {
            return id
        }
        if (!settled.value) {
            return LOADING_MODEL_LABEL
        }
        return failed.value ? UNKNOWN_MODEL_LABEL : UNLISTED_MODEL_LABEL
    }

    /**
     * A saved model as the Model field shows it: the option label when listed. Unlisted, Advanced shows the ID and
     * Standard says `Unlisted model` (`Saved model` if the list could not be read); Standard shows `…` until the list
     * has answered.
     */
    function selectedModelLabel(id: string, mode: UiMode): string {
        const listed = findModel(id)
        return listed ? modelOptionLabel(listed, mode) : unlistedModelLabel(id, mode)
    }

    /**
     * A saved model as a chip names it: the exact ID in Advanced; in Standard the listed name, `Unlisted model` when
     * the list lacks it, `Saved model` when the list could not be read, and `…` until the list has answered.
     */
    function modelName(id: string, mode: UiMode): string {
        if (mode === 'advanced') {
            return id
        }
        return findModel(id)?.name ?? unlistedModelLabel(id, mode)
    }

    return {
        models,
        defaultModel,
        settled,
        failed,
        loadLlmModels,
        findModel,
        recommendedModelLabel,
        recommendedLabel,
        selectedModelLabel,
        modelName,
    }
}
